import { onMounted, watch } from 'vue';
import { useStore } from '@/store/BookingStore';
import { CatalogManagerApi } from '@/service/CatalogApiService/CatalogManagerApi';
import { FamilyId, type Option } from '../../../requests-module/src';

async function fetchServicesInfo(store: ReturnType<typeof useStore>, options: Option[] | null | undefined) {
    if (!options?.length) return;

    const widgetModeEnabled = store.getters.getIsWidgetMode;
    const hasTrainOptions = options.some(
        (opt) =>
            Number(opt.familyId) === FamilyId.TRAIN ||
            Number(opt.familyId) === FamilyId.TRAIN_SANS_COMPARTIMENT,
    );

    const serviceIds = Array.from(
        new Set(
            options
                .map((option) => option.prestId)
                .filter((id): id is number => id != null && id !== undefined),
        ),
    );

    if (!serviceIds.length) return;

    // Logs are meant to be used for debugging the widget TNE class-card issue.
    // We keep logs conditional (widget + train options) to avoid spamming every page.
    if (widgetModeEnabled && hasTrainOptions) {
        const trainPrestIds = options
            .filter(
                (opt) =>
                    Number(opt.familyId) === FamilyId.TRAIN ||
                    Number(opt.familyId) === FamilyId.TRAIN_SANS_COMPARTIMENT,
            )
            .map((opt) => opt.prestId)
            .filter((id): id is number => id != null && id !== undefined)
            .map(String);

        const uniqueTrainPrestIds = Array.from(new Set(trainPrestIds)).sort();

        const uniqueServiceIds = Array.from(new Set(serviceIds.map(String)));
        const serviceIdsTopDuplicates = serviceIds
            .map(String)
            .reduce<Record<string, number>>((acc, id) => {
                acc[id] = (acc[id] ?? 0) + 1;
                return acc;
            }, {});
        const topDuplicates = Object.entries(serviceIdsTopDuplicates)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        console.log('[useServicesInfo][widget][TNE] fetching /services/info', {
            locale: store.getters.getLocale,
            serviceIdsTotal: serviceIds.length,
            serviceIdsUniqueCount: uniqueServiceIds.length,
            trainPrestIdsUnique: uniqueTrainPrestIds,
            topDuplicatePrestIds: topDuplicates, // [prestId, count]
        });
    }

    const catalogManagerApi = new CatalogManagerApi(store.getters.getLocale);
    const services = await catalogManagerApi.getServiceContent(serviceIds);

    if (widgetModeEnabled && hasTrainOptions) {
        console.log('[useServicesInfo][widget][TNE] /services/info response', {
            servicesCount: services?.length ?? null,
        });
    }

    store.commit('setServicesInfo', Array.from(services) as any);
}

/** Re-load Moel for the booking currently in the store (e.g. after train-step refresh). */
export async function refetchServicesInfoForCurrentBooking(
    storeInstance: ReturnType<typeof useStore>,
): Promise<void> {
    await fetchServicesInfo(storeInstance, storeInstance.getters.getBooking?.options);
}

type UseServicesInfoOptions = {
    /** Fetch once on mount (default: true). Used app-wide so summary has labels on every page. */
    fetchOnMount?: boolean;
    /** Re-fetch when booking options change (default: false). Only needed on the options page. */
    watchOptionsChanges?: boolean;
};

/**
 * Loads catalog service labels/criteria (Moel) for booking options into the store.
 */
export function useServicesInfo({
    watchOptionsChanges = false,
}: UseServicesInfoOptions = {}) {
    const store = useStore();

    onMounted(() => {
        void fetchServicesInfo(store, store.getters.getBooking?.options);
    });


    if (watchOptionsChanges) {
        watch(
            () => {
                const opts = store.state.booking?.options ?? [];
                return opts
                    .map((o) => o.prestId)
                    .filter((id): id is number => id != null && id !== undefined)
                    .sort((a, b) => a - b)
                    .join(',');
            },
            () => {
                void fetchServicesInfo(store, store.state.booking?.options ?? null);
            },
        );
    }
}
